import { z } from 'zod';

import { type CountryCode, CountryCodeSchema } from '../../questions/country-code/country-code';

/**
 * A set of countries as a setting stores it: `{ SA: true, AE: true }`.
 *
 * A record rather than an array because an object-valued setting merges its
 * stored layer over the coded default (P26b), and the merge is what makes a
 * partial override legitimate. `partialRecord`, since `z.record` over an enum
 * is exhaustive in Zod 4. Empty means **any country** — the coded default of
 * every `country-set` key stays `{}` so that a stored layer can only ever add.
 */
export const CountrySetSchema = z.partialRecord(CountryCodeSchema, z.literal(true));

export type CountrySet = z.infer<typeof CountrySetSchema>;

/** The set as a sorted list, or `null` for the empty set — "any country" reads as one value. */
export function countrySetToList(set: CountrySet): CountryCode[] | null {
  const codes = Object.keys(set)
    .filter((code): code is CountryCode => set[code as CountryCode] === true)
    .sort();

  return codes.length === 0 ? null : codes;
}

export function listToCountrySet(codes: readonly CountryCode[]): CountrySet {
  return Object.fromEntries(codes.map((code) => [code, true] as const));
}

/** Whether the policy admits a country: an empty set admits every one. */
export function countrySetAllows(set: CountrySet, country: CountryCode): boolean {
  return countrySetToList(set)?.includes(country) ?? true;
}
